import React, { useMemo, useState, useRef, useEffect } from "react";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import {
  Box,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
  DialogContentText,
  MenuItem,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import { RichTreeView } from "@mui/x-tree-view/RichTreeView";
import { useNavigate } from "react-router-dom";
import Back from "../../../../../components/Back";
import Title from "../../../../../components/Title";
import FormattedDateCell from "../../../../../components/FormattedDateCell";
import {
  useProductCatalog,
  useDeleteTagType,
} from "../../../../../api/tags/tags";
import {
  buildTree,
  findNodeWithChildren,
  mapToTreeView,
  TreeNode,
} from "./utils/tree";
import { useSnackbar } from "../../../../../context/SnackbarContext";
import { useLoader } from "../../../../../context/LoaderContext";

export default function ProductCatalog() {
  const navigate = useNavigate();
  const {
    data: flatData = [],
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
  } = useProductCatalog();
  const deleteTagType = useDeleteTagType();

  const cleanedFlatData = useMemo(
    () =>
      flatData.map((item) => ({
        ...item,
        id: Number(item.id),
        parent: item.parent !== null ? Number(item.parent) : null,
      })),
    [flatData]
  );

  const tree = useMemo(() => buildTree(cleanedFlatData), [cleanedFlatData]);
  const treeRef = useRef<TreeNode[]>([]);
  useEffect(() => {
    treeRef.current = tree;
  }, [tree]);

  const tableData = useMemo(
    () =>
      cleanedFlatData
        .filter((item) => item.parent === null)
        .map((prod) => ({
          id: prod.id,
          name: prod.name,
          created_at: prod.created_at,
          updated_at: prod.updated_at,
        })),
    [cleanedFlatData]
  );

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewItem, setPreviewItem] = useState<TreeNode | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toDelete, setToDelete] = useState<string | number | null>(null);
  const [previewMode, setPreviewMode] = useState<"tree">("tree");

  const { showSnackbar } = useSnackbar();
  const { showLoader, hideLoader } = useLoader();

  const handleCreate = () => navigate(`/management/products-type/create`);
  const handleEdit = (id: string | number) =>
    navigate(`/management/products-type/edit?id=${id}`);

  const handlePreview = (item: any) => {
    const id = Number(item.id);
    const node = findNodeWithChildren(treeRef.current, id);
    setPreviewItem(node || null);
    setPreviewOpen(true);
  };

  const handleClosePreview = () => setPreviewOpen(false);

  const handleDeleteClick = (id: string | number) => {
    setToDelete(id);
    setConfirmOpen(true);
  };
  const handleConfirmDelete = async () => {
    if (toDelete !== null) {
      showLoader();
      try {
        await deleteTagType.mutateAsync(toDelete);
        showSnackbar("Product deleted!", "success");
        await refetch();
      } catch (e: any) {
        showSnackbar(e?.message || "Failed to delete", "error");
      }
      hideLoader();
    }
    setConfirmOpen(false);
    setToDelete(null);
  };
  const handleCancelDelete = () => {
    setConfirmOpen(false);
    setToDelete(null);
  };

  const columns = useMemo(
    () => [
      { accessorKey: "id", header: "ID" },
      { accessorKey: "name", header: "Product Name" },
      {
        id: "Preview",
        header: "Preview",
        size: 140,
        Cell: ({ row }) => (
          <Box>
            <Tooltip title="Preview">
              <IconButton onClick={() => handlePreview(row.original)}>
                <VisibilityIcon />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
    ],
    []
  );

  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [sorting, setSorting] = useState([]);
  const [columnFilters, setColumnFilters] = useState([]);

  const table = useMaterialReactTable({
    columns,
    data: tableData,
    mrtTheme: (theme) => ({
      baseBackgroundColor: theme.palette.background.default,
    }),
    muiTableContainerProps: {
      sx: { maxHeight: "calc(78vh - 188px)" },
    },
    muiPaginationProps: {
      color: "secondary",
      rowsPerPageOptions: [10, 25, 50, 100],
      shape: "rounded",
      variant: "outlined",
    },
    paginationDisplayMode: "pages",
    columnFilterDisplayMode: "popover",
    initialState: {
      pagination: { pageSize: 25, pageIndex: 0 },
      density: "compact",
      showGlobalFilter: true,
      columnPinning: {
        right: ["mrt-row-actions"],
      },
      sorting: [
        {
          id: "id",
          desc: false,
        },
      ],
      columnVisibility: { sys_result: false },
    },
    enableColumnResizing: true,
    enableColumnOrdering: false,
    enableStickyHeader: true,
    enableRowActions: true,
    enableFacetedValues: true,
    manualPagination: false,
    layoutMode: "grid-no-grow",
    renderTopToolbarCustomActions: () => (
      <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
        <Button variant="contained" onClick={handleCreate}>
          Create Product Tag Type
        </Button>
        <Tooltip title="Refresh">
          <span>
            <IconButton
              onClick={async () => {
                showLoader();
                try {
                  await refetch();
                } catch (e: any) {
                  showSnackbar(
                    "Failed to refresh: " + (e?.message || ""),
                    "error"
                  );
                }
                hideLoader();
              }}
              disabled={isFetching}
            >
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    ),
    muiTableBodyRowProps: {
      children: isLoading ? (
        <tr>
          <td colSpan={columns.length} align="center">
            <CircularProgress size={28} sx={{ my: 2 }} />
          </td>
        </tr>
      ) : isError ? (
        <tr>
          <td colSpan={columns.length} align="center">
            <Typography color="error" p={2}>
              {error?.message || "Failed to load data"}
            </Typography>
          </td>
        </tr>
      ) : undefined,
    },
    renderRowActionMenuItems: ({ row, closeMenu }) => [
      <MenuItem
        key="edit"
        onClick={() => {
          handleEdit(row.original.id);
          closeMenu();
        }}
      >
        Edit
      </MenuItem>,
      <MenuItem
        key="delete"
        onClick={() => {
          handleDeleteClick(row.original.id);
          closeMenu();
        }}
        sx={{ color: "error.main" }}
      >
        Delete
      </MenuItem>,
    ],
  });

  const treeItems = useMemo(
    () => (previewItem ? mapToTreeView([previewItem]) : []),
    [previewItem]
  );

  return (
    <>
      <Title>Products Tags Overview</Title>
      <MaterialReactTable table={table} />
      <Back />

      <Dialog
        open={previewOpen}
        onClose={handleClosePreview}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Product Type Preview</DialogTitle>
        <DialogContent dividers>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={1}
          >
            <Typography variant="subtitle1">View Mode</Typography>
            <ToggleButtonGroup
              value={previewMode}
              exclusive
              size="small"
              onChange={(_, val) => val && setPreviewMode(val)}
            >
              <ToggleButton value="tree">Tree</ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <Box
            sx={{
              border: "1px solid #ddd",
              borderRadius: 1,
              p: 2,
              minHeight: 300,
            }}
          >
            <RichTreeView
              items={treeItems}
              defaultExpandedItems={previewItem ? [previewItem.id] : []}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePreview}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmOpen} onClose={handleCancelDelete}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this product? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>Cancel</Button>
          <Button
            color="error"
            onClick={handleConfirmDelete}
            disabled={deleteTagType.isPending}
          >
            {deleteTagType.isPending ? (
              <CircularProgress size={18} />
            ) : (
              "Delete"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
